import { memo } from 'react';

import { bucketFromStatus, type GatewayBucket } from '@/lib/gatewayStatus';

/**
 * Shared badge component rendering the 4-state gateway bucket.
 *
 * Plan 3 (assign-agency-to-resource) Task 7. Consumed by:
 *   - admin Resources expandable row (per-hosted-agency badge)
 *   - admin Agencies "Gateway" column
 *   - user agency Settings "Gateway Status" card
 *
 * The bucket is computed client-side from the raw status string so the
 * UI is responsive without waiting for the backend's worst-status-wins
 * aggregation (which only runs on list endpoints). The backend's
 * pre-computed gateway_status field is also accepted for cases where
 * the agency-scoped view already carries it.
 */
interface GatewayStatusBadgeProps {
  /** Raw AgencyGateway.status string OR pre-computed 4-state bucket. */
  status: string | null | undefined;
  /** Raw AgencyGateway.health_status. Composite rule applied when status='running'. */
  healthStatus?: string | null | undefined;
  /** Optional last-health-check timestamp for a relative-time hint. */
  lastHealthCheck?: string | null | undefined;
  /** Visual size variant. Default 'sm'. */
  size?: 'sm' | 'md';
  /**
   * Plan 3 Task 5 (Decision 6 pending state): when true, overrides the
   * bucket label with "Restarting" and forces the spinner on, regardless
   * of the raw status. Used by AgenciesManagement after the restart API
   * returns 200 — the row optimistically flips to a "pending" visual
   * while polling waits for gateway_status='running'.
   */
  restarting?: boolean;
}

const BUCKET_CONFIG: Record<
  GatewayBucket,
  { label: string; classes: string; spinner?: boolean }
> = {
  running: {
    label: 'Running',
    classes: 'bg-green-100 text-green-800',
  },
  provisioning: {
    label: 'Provisioning',
    classes: 'bg-yellow-100 text-yellow-800',
    spinner: true,
  },
  degraded: {
    label: 'Degraded',
    classes: 'bg-amber-100 text-amber-800',
  },
  down: {
    label: 'Down',
    classes: 'bg-red-100 text-red-800',
  },
};

function formatRelativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function GatewayStatusBadgeImpl({
  status,
  healthStatus,
  lastHealthCheck,
  size = 'sm',
  restarting = false,
}: GatewayStatusBadgeProps) {
  const bucket = bucketFromStatus(status, healthStatus);
  const padding = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';

  if (!bucket) {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full bg-gray-100 ${padding} text-gray-600`}
        aria-label="No gateway status available"
        data-testid="gateway-status-badge-none"
      >
        —
      </span>
    );
  }

  // Plan 3 Task 5 Decision 6 pending state: when an admin triggers a
  // restart, the row optimistically flips to "Restarting" with a spinner
  // regardless of the underlying bucket. The status has been flipped to
  // 'pending' on the server, but the visual override guarantees the
  // spinner even before the next list refresh lands.
  if (restarting) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full bg-yellow-100 text-yellow-800 ${padding}`}
        data-testid="gateway-status-badge-restarting"
        role="status"
        aria-label="Gateway Restarting"
      >
        <svg
          className="h-3 w-3 animate-spin"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
        <span>Restarting</span>
      </span>
    );
  }

  const config = BUCKET_CONFIG[bucket];
  const relativeTime = formatRelativeTime(lastHealthCheck);
  const testid = `gateway-status-badge-${bucket}`;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full ${config.classes} ${padding}`}
      data-testid={testid}
      role="status"
      aria-label={`Gateway ${config.label}${
        relativeTime ? `, checked ${relativeTime}` : ''
      }`}
    >
      {config.spinner && (
        <svg
          className="h-3 w-3 animate-spin"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      )}
      <span>{config.label}</span>
      {relativeTime && (
        <span className="text-[10px] opacity-70" aria-hidden="true">
          {relativeTime}
        </span>
      )}
    </span>
  );
}

export const GatewayStatusBadge = memo(GatewayStatusBadgeImpl);
